import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import { useCart } from "../context/CartContext";
import Toast from "../components/Toast";

const DetailsWrapper = styled.div`
  min-height: 100vh;
  background: var(--background-light);
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Breadcrumb = styled.div`
  margin-bottom: 20px;
  font-size: 0.9rem;

  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;

    &:hover {
      color: var(--secondary-color);
    }
  }

  span {
    margin: 0 10px;
    color: rgba(255, 255, 255, 0.6);
  }
`;

const ProductSection = styled.section`
  padding: 80px 0;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 60px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ImageGallery = styled.div`
  position: sticky;
  top: 120px;
  height: fit-content;
`;

const MainImage = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 20px;
  background: white;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 350px;
  }
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
`;

const Thumbnail = styled.div`
  height: 100px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid
    ${(props) => (props.active ? "var(--secondary-color)" : "transparent")};
  transition: all 0.3s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    border-color: var(--secondary-color);
  }
`;

const ProductInfo = styled.div`
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: var(--secondary-color);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 15px;
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Inter", sans-serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ProductDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  line-height: 1.8;
  margin-bottom: 30px;
`;

const FeaturesList = styled.div`
  margin-bottom: 30px;

  h3 {
    font-size: 1.3rem;
    color: var(--text-dark);
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 15px;
      font-size: 1rem;
      color: var(--text-light);

      .icon {
        color: var(--secondary-color);
        font-weight: bold;
        font-size: 1.2rem;
        width: 24px;
        text-align: center;
      }
    }
  }
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AddToCartBtn = styled.button`
  padding: 18px;
  background: ${(props) => (props.inCart ? "#10b981" : "var(--primary-color)")};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background: ${(props) => (props.inCart ? "#059669" : "#0c3a63")};
    transform: translateY(-2px);
  }
`;

const QuoteBtn = styled.button`
  padding: 18px;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }
`;

const InfoBox = styled.div`
  background: var(--background-light);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 30px;

  h4 {
    color: var(--text-dark);
    margin-bottom: 15px;
    font-size: 1.1rem;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .info-item {
    strong {
      display: block;
      color: var(--text-dark);
      font-size: 0.9rem;
      margin-bottom: 5px;
    }

    span {
      color: var(--text-light);
      font-size: 0.95rem;
    }
  }
`;

const TabSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 60px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 20px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 30px;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 15px 25px;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${(props) =>
    props.active ? "var(--secondary-color)" : "var(--text-light)"};
  cursor: pointer;
  border-bottom: 3px solid
    ${(props) => (props.active ? "var(--secondary-color)" : "transparent")};
  margin-bottom: -2px;
  transition: all 0.3s ease;

  &:hover {
    color: var(--secondary-color);
  }
`;

const TabContent = styled.div`
  h3 {
    color: var(--text-dark);
    margin-bottom: 20px;
  }

  p {
    color: var(--text-light);
    line-height: 1.8;
    margin-bottom: 15px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      padding: 12px 0;
      border-bottom: 1px solid #e5e7eb;
      color: var(--text-light);

      &:last-child {
        border-bottom: none;
      }

      strong {
        color: var(--text-dark);
        margin-right: 10px;
      }
    }
  }
`;

const RelatedSection = styled.section`
  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 40px;
    text-align: center;
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
`;

const RelatedCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const RelatedImage = styled.div`
  height: 200px;
  background: #f8f9fa;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RelatedContent = styled.div`
  padding: 20px;

  h3 {
    font-size: 1.1rem;
    color: var(--text-dark);
    margin-bottom: 10px;
  }

  p {
    color: var(--text-light);
    font-size: 0.9rem;
  }
`;

const ReviewsSection = styled.section`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 60px;

  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 30px;
  }
`;

const ReviewsSummary = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 40px;
  padding-bottom: 30px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const AverageRating = styled.div`
  text-align: center;

  .rating-number {
    font-size: 3.5rem;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 10px;
  }

  .stars {
    font-size: 1.5rem;
    color: #fbbf24;
    margin-bottom: 10px;
  }

  .total-reviews {
    font-size: 0.9rem;
    color: var(--text-light);
  }
`;

const RatingBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RatingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  .stars {
    min-width: 80px;
    font-size: 0.9rem;
    color: var(--text-light);
  }

  .bar {
    flex: 1;
    height: 8px;
    background: #e5e7eb;
    border-radius: 10px;
    overflow: hidden;

    .fill {
      height: 100%;
      background: #fbbf24;
      transition: width 0.3s ease;
    }
  }

  .count {
    min-width: 40px;
    font-size: 0.9rem;
    color: var(--text-light);
    text-align: right;
  }
`;

const ReviewForm = styled.div`
  background: var(--background-light);
  padding: 30px;
  border-radius: 15px;
  margin-bottom: 30px;

  h3 {
    font-size: 1.3rem;
    color: var(--text-dark);
    margin-bottom: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--text-dark);
    font-size: 0.95rem;
  }

  input,
  textarea {
    width: 100%;
    padding: 12px 15px;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: var(--secondary-color);
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

const StarRating = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;

  .star {
    font-size: 2rem;
    color: ${(props) => (props.hoverRating ? "#fbbf24" : "#e5e7eb")};
    cursor: pointer;
    transition: all 0.2s ease;

    &.filled {
      color: #fbbf24;
    }

    &:hover {
      transform: scale(1.2);
    }
  }
`;

const SubmitReviewButton = styled.button`
  padding: 12px 30px;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const ReviewItem = styled.div`
  padding: 25px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const ReviewerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  .avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--secondary-color);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.2rem;
  }

  .info {
    .name {
      font-weight: 600;
      color: var(--text-dark);
      margin-bottom: 5px;
    }

    .date {
      font-size: 0.85rem;
      color: var(--text-light);
    }
  }
`;

const ReviewRating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  .stars {
    color: #fbbf24;
    font-size: 1rem;
  }
`;

const ReviewText = styled.p`
  color: var(--text-light);
  line-height: 1.8;
  margin: 0;
`;

const NoReviews = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-light);

  i {
    font-size: 3rem;
    margin-bottom: 15px;
    opacity: 0.5;
  }

  p {
    font-size: 1.1rem;
  }
`;

const ContainerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const [activeTab, setActiveTab] = useState("details");
  const [activeImage, setActiveImage] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [container, setContainer] = useState(null);

  // Review states
  const [reviews, setReviews] = useState([
    {
      id: 1,
      userName: "John Smith",
      rating: 5,
      date: "January 15, 2026",
      comment:
        "Excellent quality container! Exactly as described and delivery was prompt. Highly recommend for storage needs.",
    },
    {
      id: 2,
      userName: "Sarah Johnson",
      rating: 4,
      date: "January 10, 2026",
      comment:
        "Great container, very sturdy. Minor scratches but overall in good condition. Perfect for my workshop conversion.",
    },
    {
      id: 3,
      userName: "Mike Davis",
      rating: 5,
      date: "December 28, 2025",
      comment:
        "Outstanding service and product quality. The container arrived in perfect condition and was placed exactly where I needed it.",
    },
  ]);
  const [newReview, setNewReview] = useState({
    userName: "",
    email: "",
    rating: 0,
    comment: "",
  });
  const [hoverRating, setHoverRating] = useState(0);

  // All containers data (same as in Containers.js)
  const allContainers = [
    {
      id: 1,
      title: "10FT Shipping Container",
      category: "new",
      image: "/assets/2025/08/10ft-shipping-container-300x300.jpg",
      description: "Perfect for small storage needs and tight spaces.",
      features: [
        "Dimensions: 10' x 8' x 8.5'",
        "Wind and watertight",
        "Steel construction",
        "Cargo doors",
      ],
      fullDescription:
        "Our 10FT shipping containers are perfect for residential use, offering secure storage in a compact footprint. These containers are ideal for backyards, construction sites, or anywhere space is limited.",
      specifications: {
        length: "10 feet",
        width: "8 feet",
        height: "8.5 feet",
        weight: "2,850 lbs",
        capacity: "680 cubic feet",
        doorOpening: "7'8\" x 7'6\"",
      },
    },
    {
      id: 2,
      title: "20FT Shipping Container",
      category: "new",
      image: "/assets/2025/08/20ft-shipping-container-red-300x300.jpg",
      description: "Most popular size for residential and commercial use.",
      features: [
        "Dimensions: 20' x 8' x 8.5'",
        "Wind and watertight",
        "Steel construction",
        "Cargo doors",
      ],
      fullDescription:
        "The 20FT container is our most popular model, offering excellent storage capacity while maintaining a manageable size. Perfect for both commercial and residential applications.",
      specifications: {
        length: "20 feet",
        width: "8 feet",
        height: "8.5 feet",
        weight: "5,070 lbs",
        capacity: "1,360 cubic feet",
        doorOpening: "7'8\" x 7'6\"",
      },
    },
    {
      id: 3,
      title: "40FT Shipping Container",
      category: "new",
      image: "/assets/2025/08/40ft-shipping-container-grey-300x300.jpg",
      description: "Maximum storage capacity for large projects.",
      features: [
        "Dimensions: 40' x 8' x 8.5'",
        "Wind and watertight",
        "Steel construction",
        "Cargo doors",
      ],
      fullDescription:
        "Our 40FT containers provide maximum storage space, ideal for large commercial operations, construction projects, or extensive inventory storage needs.",
      specifications: {
        length: "40 feet",
        width: "8 feet",
        height: "8.5 feet",
        weight: "8,160 lbs",
        capacity: "2,720 cubic feet",
        doorOpening: "7'8\" x 7'6\"",
      },
    },
    // Add more containers as needed
  ];

  useEffect(() => {
    const foundContainer = allContainers.find((c) => c.id === parseInt(id));
    if (foundContainer) {
      setContainer(foundContainer);
    } else {
      navigate("/containers");
    }
  }, [id]);

  if (!container) {
    return null;
  }

  const handleAddToCart = () => {
    addToCart(container);
    setShowToast(true);
  };

  const handleGetQuote = () => {
    navigate("/contact");
  };

  const handleSubmitReview = (e) => {
    e.preventDefault();

    if (newReview.rating === 0) {
      alert("Please select a rating");
      return;
    }

    const review = {
      id: reviews.length + 1,
      userName: newReview.userName,
      rating: newReview.rating,
      date: new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      comment: newReview.comment,
    };

    setReviews([review, ...reviews]);
    setNewReview({ userName: "", email: "", rating: 0, comment: "" });
    setShowToast(true);
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getRatingCount = (rating) => {
    return reviews.filter((review) => review.rating === rating).length;
  };

  const getRatingPercentage = (rating) => {
    if (reviews.length === 0) return 0;
    return (getRatingCount(rating) / reviews.length) * 100;
  };

  const relatedContainers = allContainers
    .filter((c) => c.category === container.category && c.id !== container.id)
    .slice(0, 3);

  const images = [container.image, container.image, container.image]; // You can add more images

  return (
    <DetailsWrapper>
      {showToast && (
        <Toast
          title="Added to Cart"
          message={`${container.title} added to cart!`}
          type="success"
          onClose={() => setShowToast(false)}
        />
      )}

      <HeroSection>
        <Container>
          <Breadcrumb>
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/containers">Containers</Link>
            <span>/</span>
            <span style={{ color: "white" }}>{container.title}</span>
          </Breadcrumb>
        </Container>
      </HeroSection>

      <ProductSection>
        <Container>
          <ProductGrid>
            <ImageGallery>
              <MainImage>
                <img src={images[activeImage]} alt={container.title} />
              </MainImage>
              <ThumbnailGrid>
                {images.map((img, index) => (
                  <Thumbnail
                    key={index}
                    active={activeImage === index}
                    onClick={() => setActiveImage(index)}
                  >
                    <img src={img} alt={`${container.title} ${index + 1}`} />
                  </Thumbnail>
                ))}
              </ThumbnailGrid>
            </ImageGallery>

            <ProductInfo>
              <CategoryBadge>{container.category}</CategoryBadge>
              <ProductTitle>{container.title}</ProductTitle>
              <ProductDescription>
                {container.fullDescription || container.description}
              </ProductDescription>

              <FeaturesList>
                <h3>Key Features</h3>
                <ul>
                  {container.features.map((feature, index) => (
                    <li key={index}>
                      <span className="icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </FeaturesList>

              <ActionButtons>
                <AddToCartBtn
                  inCart={isInCart(container.id)}
                  onClick={handleAddToCart}
                >
                  <i
                    className={`fas fa-${isInCart(container.id) ? "check" : "cart-plus"}`}
                  ></i>
                  {isInCart(container.id) ? "In Cart" : "Add to Cart"}
                </AddToCartBtn>
                <QuoteBtn onClick={handleGetQuote}>
                  <i className="fas fa-envelope"></i>
                  Request Quote
                </QuoteBtn>
              </ActionButtons>

              {container.specifications && (
                <InfoBox>
                  <h4>Specifications</h4>
                  <div className="info-grid">
                    {Object.entries(container.specifications).map(
                      ([key, value]) => (
                        <div className="info-item" key={key}>
                          <strong>
                            {key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}
                          </strong>
                          <span>{value}</span>
                        </div>
                      ),
                    )}
                  </div>
                </InfoBox>
              )}

              <InfoBox>
                <h4>Need Help?</h4>
                <p style={{ margin: 0, color: "var(--text-light)" }}>
                  <i
                    className="fas fa-phone"
                    style={{
                      marginRight: "10px",
                      color: "var(--secondary-color)",
                    }}
                  ></i>
                  Call us at <strong>(484) 542-9203</strong> for expert advice
                </p>
              </InfoBox>
            </ProductInfo>
          </ProductGrid>

          <TabSection>
            <Tabs>
              <Tab
                active={activeTab === "details"}
                onClick={() => setActiveTab("details")}
              >
                Details
              </Tab>
              <Tab
                active={activeTab === "delivery"}
                onClick={() => setActiveTab("delivery")}
              >
                Delivery
              </Tab>
              <Tab
                active={activeTab === "warranty"}
                onClick={() => setActiveTab("warranty")}
              >
                Warranty
              </Tab>
            </Tabs>

            <TabContent>
              {activeTab === "details" && (
                <div>
                  <h3>Product Details</h3>
                  <p>{container.fullDescription || container.description}</p>
                  <p>
                    All our containers are built to ISO standards and are
                    designed to withstand harsh weather conditions. They feature
                    heavy-duty steel construction, secure locking mechanisms,
                    and are available in various conditions to suit your budget
                    and requirements.
                  </p>
                </div>
              )}

              {activeTab === "delivery" && (
                <div>
                  <h3>Delivery Information</h3>
                  <p>
                    We offer nationwide delivery with professional placement
                    services. Our delivery team will work with you to ensure
                    your container is positioned exactly where you need it.
                  </p>
                  <ul>
                    <li>
                      <strong>Delivery Time:</strong> 3-7 business days in most
                      areas
                    </li>
                    <li>
                      <strong>Placement:</strong> Professional positioning
                      included
                    </li>
                    <li>
                      <strong>Site Requirements:</strong> Level ground and clear
                      access required
                    </li>
                    <li>
                      <strong>Additional Services:</strong> Crane services
                      available upon request
                    </li>
                  </ul>
                </div>
              )}

              {activeTab === "warranty" && (
                <div>
                  <h3>Warranty & Support</h3>
                  <p>
                    All our containers come with a comprehensive warranty
                    covering structural integrity and weatherproofing. We stand
                    behind the quality of our products.
                  </p>
                  <ul>
                    <li>
                      <strong>Structural Warranty:</strong> Varies by container
                      condition
                    </li>
                    <li>
                      <strong>Weatherproof Guarantee:</strong> Wind and
                      watertight certification
                    </li>
                    <li>
                      <strong>Support:</strong> Lifetime customer support
                    </li>
                    <li>
                      <strong>Modifications:</strong> Warranty-compliant
                      customization options available
                    </li>
                  </ul>
                </div>
              )}
            </TabContent>
          </TabSection>

          {/* Reviews Section */}
          <ReviewsSection>
            <h2>Customer Reviews</h2>

            <ReviewsSummary>
              <AverageRating>
                <div className="rating-number">{calculateAverageRating()}</div>
                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>
                      {i < Math.round(calculateAverageRating()) ? "★" : "☆"}
                    </span>
                  ))}
                </div>
                <div className="total-reviews">{reviews.length} reviews</div>
              </AverageRating>

              <RatingBars>
                {[5, 4, 3, 2, 1].map((rating) => (
                  <RatingBar key={rating}>
                    <span className="stars">{rating} stars</span>
                    <div className="bar">
                      <div
                        className="fill"
                        style={{ width: `${getRatingPercentage(rating)}%` }}
                      />
                    </div>
                    <span className="count">{getRatingCount(rating)}</span>
                  </RatingBar>
                ))}
              </RatingBars>
            </ReviewsSummary>

            <ReviewForm>
              <h3>Write a Review</h3>
              <form onSubmit={handleSubmitReview}>
                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "10px",
                      fontWeight: 600,
                    }}
                  >
                    Your Rating
                  </label>
                  <StarRating hoverRating={hoverRating}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`star ${star <= (hoverRating || newReview.rating) ? "filled" : ""}`}
                        onClick={() =>
                          setNewReview({ ...newReview, rating: star })
                        }
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        ★
                      </span>
                    ))}
                  </StarRating>
                </div>

                <FormGroup>
                  <label htmlFor="userName">Name *</label>
                  <input
                    type="text"
                    id="userName"
                    value={newReview.userName}
                    onChange={(e) =>
                      setNewReview({ ...newReview, userName: e.target.value })
                    }
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={newReview.email}
                    onChange={(e) =>
                      setNewReview({ ...newReview, email: e.target.value })
                    }
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="comment">Your Review *</label>
                  <textarea
                    id="comment"
                    value={newReview.comment}
                    onChange={(e) =>
                      setNewReview({ ...newReview, comment: e.target.value })
                    }
                    placeholder="Share your experience with this container..."
                    required
                  />
                </FormGroup>

                <SubmitReviewButton type="submit">
                  Submit Review
                </SubmitReviewButton>
              </form>
            </ReviewForm>

            <ReviewsList>
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <ReviewItem key={review.id}>
                    <ReviewHeader>
                      <ReviewerInfo>
                        <div className="avatar">
                          {review.userName.charAt(0).toUpperCase()}
                        </div>
                        <div className="info">
                          <div className="name">{review.userName}</div>
                          <div className="date">{review.date}</div>
                        </div>
                      </ReviewerInfo>
                      <ReviewRating>
                        <span className="stars">
                          {[...Array(5)].map((_, i) => (
                            <span key={i}>{i < review.rating ? "★" : "☆"}</span>
                          ))}
                        </span>
                      </ReviewRating>
                    </ReviewHeader>
                    <ReviewText>{review.comment}</ReviewText>
                  </ReviewItem>
                ))
              ) : (
                <NoReviews>
                  <i className="fas fa-comments"></i>
                  <p>No reviews yet. Be the first to review this container!</p>
                </NoReviews>
              )}
            </ReviewsList>
          </ReviewsSection>

          {relatedContainers.length > 0 && (
            <RelatedSection>
              <h2>Related Containers</h2>
              <RelatedGrid>
                {relatedContainers.map((related) => (
                  <RelatedCard
                    key={related.id}
                    onClick={() => navigate(`/containers/${related.id}`)}
                  >
                    <RelatedImage>
                      <img src={related.image} alt={related.title} />
                    </RelatedImage>
                    <RelatedContent>
                      <h3>{related.title}</h3>
                      <p>{related.description}</p>
                    </RelatedContent>
                  </RelatedCard>
                ))}
              </RelatedGrid>
            </RelatedSection>
          )}
        </Container>
      </ProductSection>
    </DetailsWrapper>
  );
};

export default ContainerDetails;
